import clsx from 'clsx';
import type { HTMLAttributes, ReactNode } from 'react';
import { z } from 'zod';

import styles from './Grid.module.scss';

export const GRID_GAPS = ['sm', 'md', 'lg'] as const;
export const gridGapSchema = z.enum(GRID_GAPS);
export type GridGap = z.infer<typeof gridGapSchema>;

export const MAX_GRID_ROW_COLUMNS = 12;

export const GRID_ROW_ALIGNS = ['start', 'center', 'stretch'] as const;
export const gridRowAlignSchema = z.enum(GRID_ROW_ALIGNS);
export type GridRowAlign = z.infer<typeof gridRowAlignSchema>;

export const gridPropsSchema = z
  .object({
    gap: gridGapSchema.optional(),
  })
  .strict();

export type GridDataProps = z.infer<typeof gridPropsSchema>;

export interface GridProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'children'>,
    GridDataProps {
  children?: ReactNode;
}

const gapClassMap: Record<GridGap, string> = {
  sm: styles.gapSm,
  md: styles.gapMd,
  lg: styles.gapLg,
};

/**
 * Layout primitive for irregular multi-row grids. Stacks `GridRow` {@link GridRow}
 * children vertically with token-aligned spacing; each row lays out
 * its children in equally-sized columns.
 */
export const Grid = ({
  gap = 'md',
  className,
  children,
  ...rest
}: GridProps) => (
  <div
    data-testid="grid"
    className={clsx(styles.root, gapClassMap[gap], className)}
    {...rest}
  >
    {children}
  </div>
);
